<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';

const props = defineProps<{
  show: boolean;
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
}>();

const emit = defineEmits(['close']);

let timer: any;

onMounted(() => {
  if (props.duration && props.show) {
    timer = setTimeout(() => emit('close'), props.duration);
  }
});

// Watch show prop to restart timer if needed? 
// For simplicity, we assume the parent manages visibility and re-triggering logic, 
// but a "Senior" toast usually manages its own queue. 
// However, a simple controlled component is fine for this scope.
</script>

<template>
  <Transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
    enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="fixed bottom-4 right-4 z-50 max-w-sm w-full bg-surface-light border shadow-2xl rounded-xl pointer-events-auto overflow-hidden ring-1 ring-black/5 backdrop-blur-3xl"
      :class="{
        'border-green-500/50 bg-black/90': type === 'success',
        'border-red-500/50 bg-black/90': type === 'error',
        'border-blue-500/50 bg-black/90': type === 'info' || !type
      }"
      role="alert"
    >
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <!-- Icons -->
            <svg v-if="type === 'success'" class="h-6 w-6 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
            <svg v-if="type === 'error'" class="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" /></svg>
            <svg v-if="type === 'info' || !type" class="h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm font-medium text-white">{{ type === 'error' ? '¡ Atención !' : (type === 'success' ? '¡ Éxito !' : 'Información') }}</p>
            <p class="mt-1 text-sm text-gray-400">{{ message }}</p>
          </div>
          <div class="ml-4 flex flex-shrink-0">
            <button @click="$emit('close')" type="button" class="inline-flex rounded-md bg-transparent text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2">
              <span class="sr-only">Cerrar</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
